import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, ClickableUploadIcon, ErrorResponse, Toolbar } from '~components';
import Field from '~components/form-fields/Field';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faFileImport, faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view';
import AddModal from '~support-at-home/components/common/AddModal';

import { SahClientContributionsGrouped } from '~support-at-home/models';

import columnsFactory from '~support-at-home/forms/client-contributions/listAll';
import searchFields from '~support-at-home/forms/client-contributions/search';

import { getAll } from '~main/actions/clients';
import { create as createContribution, getGrouped, uploadFile } from '~support-at-home/actions/clientContributions';
import { getAllSah } from '~support-at-home/actions/fundingTypes';
import { getAll as getContributionCategories } from '~lookups/actions/sahCategories';

import { clear, clearError } from '~support-at-home/reducers/clientContributions';

import { selectSahCategories } from '~lookups/selectors/sahCategories';
import { selectLookupClients } from '~main/selectors/clients/clients';
import {
  selectErrorResponse,
  selectLoading,
  selectSahClientContributionsGrouped,
  selectUploadError,
} from '~support-at-home/selectors/clientContributions';
import { selectSahFundingTypes } from '~support-at-home/selectors/fundingTypes';

import ContributionsModal from './ContributionsModal';

interface NewContribution {
  client_id: string;
  category_id: string;
  funding_source_id: number | null;
  contrib_proportion: number | null;
  start_date: string;
  end_date: string;
}

const Contributions: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isClientOpen, setClientOpen] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const [data, setData] = useState<SahClientContributionsGrouped[]>([]);
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('sah-client-contributions-search', {});
  const search = (values: { [key: string]: any }) => setSearchParams(values);
  const [isAddModalOpen, setAddModalOpen] = useState(false);

  const [newContribution, setNewContribution] = useState<NewContribution>({
    client_id: '',
    category_id: '',
    funding_source_id: null,
    contrib_proportion: null,
    start_date: '',
    end_date: '',
  });

  const isAddValid =
    !!newContribution.client_id &&
    !!newContribution.category_id &&
    newContribution.funding_source_id != null &&
    Number.isFinite(newContribution.funding_source_id) &&
    newContribution.contrib_proportion !== null &&
    !isNaN(newContribution.contrib_proportion) &&
    newContribution.contrib_proportion >= 0 &&
    !!newContribution.start_date &&
    !!newContribution.end_date;

  const categories = useAppSelector(selectSahCategories);
  const fundingSources = useAppSelector(selectSahFundingTypes);
  const openAddModal = () => {
    setNewContribution({
      client_id: '',
      category_id: '',
      funding_source_id: null,
      contrib_proportion: null,
      start_date: '',
      end_date: '',
    });
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setTimeout(() => {
      load();
    }, 0);
  };

  const handleAddContributionChange = (name: string, value: any) => {
    setNewContribution((prev) => ({ ...prev, [name as keyof NewContribution]: value }));
  };

  const handleCategoryChange = (name: string, value: any) => {
    handleAddContributionChange(name, value);
    setNewContribution((prev) => ({ ...prev, contrib_proportion: null }));
  };

  const handleContributionProportionChange = (name: string, value: any) => {
    if (value === '' || value === null || value === undefined) {
      value = null;
    } else {
      const numValue = parseFloat(value);
      if (
        !isNaN(numValue) &&
        numValue >= 0 &&
        numValue <= Number(categories.find((x) => x.id === newContribution.category_id)?.max_contrib_proportion)
      ) {
        value = numValue;
      } else return;
    }

    handleAddContributionChange(name, value);
  };

  const handleAddContributionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAddValid) return;
    await dispatch(createContribution(newContribution)).unwrap();
    await load();
    setAddModalOpen(false);
  };

  const loading = useAppSelector(selectLoading) === 'pending';
  const groupedContributions = useAppSelector(selectSahClientContributionsGrouped);
  const errorResponse = useAppSelector(selectErrorResponse);
  const uploadError = useAppSelector(selectUploadError);

  const toggleModal = (toggle: boolean, clientId: number | null = null) => {
    setSelectedClientId(clientId);
    setClientOpen(toggle);
  };

  const onModalClose = () => {
    toggleModal(false);
    load();
  };

  const load = async () => {
    dispatch(clearError([]));
    dispatch(getGrouped({ ...searchParams }));
  };

  const onUpload = async (file: File, setProgress: (value: number) => void) => {
    try {
      await dispatch(uploadFile({ file, setProgress })).unwrap();
      await load();
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const columns = columnsFactory((id) => toggleModal(true, id));

  useEffect(() => {
    dispatch(getAllSah());
    dispatch(getContributionCategories({ sort: ['sequence'] }));
    load();
    return () => {
      dispatch(clear([]));
    };
  }, []);

  useEffect(() => {
    load();
  }, [searchParams]);

  useEffect(() => {
    setData(groupedContributions as SahClientContributionsGrouped[]);
  }, [groupedContributions]);

  return (
    <div className="p-2">
      <ErrorResponse message={errorResponse} dataCy="sah-client-contributions-error" />
      <Toolbar
        id="contributions"
        rightCluster={
          <>
            <ClickableUploadIcon
              id="sah-client-contributions-file-upload-button"
              icon={faFileImport}
              toolTip="Import CSV / JSON file"
              onUpload={onUpload}
              externalError={uploadError}
            />
            <ClickableIcon
              id="sah-client-contributions-search-button"
              icon={faMagnifyingGlass}
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Button id="sah-client-contributions-add-button" size="sm" color="success" onClick={openAddModal}>
              <Icon className="me-2" icon={faPlus} />
              Add Contribution
            </Button>
          </>
        }
      />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} onSubmit={search} />
      <TableView
        id="sah-client-contributions-grouped-table"
        columns={columns}
        data={data}
        emptyMessage={'No client SAH Contributions found'}
        isLoading={loading}
        readonly
      />
      <ContributionsModal id={selectedClientId} isOpen={isClientOpen} onClose={onModalClose} />

      <AddModal
        isOpen={isAddModalOpen}
        title="Add New Contribution"
        onClose={closeAddModal}
        onSubmit={handleAddContributionSubmit}
        submitLabel="Add Contribution"
        submitDisabled={!isAddValid}
      >
        <Field
          id="client_id"
          caption="Client"
          type="select-lazy"
          value={newContribution.client_id}
          selectorOptions={selectLookupClients}
          selectorActions={getAll}
          required
          fieldId="client_id"
          handleChange={handleAddContributionChange}
        />
        <Field
          id="category_id"
          caption="Category"
          type="select"
          value={newContribution.category_id}
          options={categories.map((c: any) => ({ id: c.id, name: c.name }))}
          handleChange={handleCategoryChange}
          fieldId="category_id"
          required
        />
        <Field
          id="funding_source_id"
          caption="Funding Source"
          type="select"
          value={newContribution.funding_source_id}
          selectorActions={getAllSah}
          options={fundingSources.map((f: any) => ({ id: f.id, name: f.name }))}
          handleChange={handleAddContributionChange}
          fieldId="funding_source_id"
          required
        />
        <Field
          id="contrib_proportion"
          caption="Contribution"
          type="number"
          value={newContribution.contrib_proportion}
          handleChange={handleContributionProportionChange}
          fieldId="contrib_proportion"
          disabled={!newContribution.category_id}
          warning={
            newContribution.category_id
              ? `Maximum contribution proportion ${categories.find((x) => x.id === newContribution.category_id)?.max_contrib_proportion}%`
              : ''
          }
          required
        />
        <Field
          id="start_date"
          caption="Start Date"
          type="date"
          value={newContribution.start_date}
          handleChange={handleAddContributionChange}
          fieldId="start_date"
          required
        />
        <Field
          id="end_date"
          caption="End Date"
          type="date"
          value={newContribution.end_date}
          handleChange={handleAddContributionChange}
          fieldId="end_date"
          required
        />
      </AddModal>
    </div>
  );
};

export default Contributions;
